<template>
  <div class="international-phone-field" :class="{ 'phone-invalid': showError }">
    <VueTelInput
      v-model="localPhone"
      :default-country="defaultCountry"
      :auto-default-country="false"
      mode="international"
      :valid-characters-only="true"
      :auto-format="true"
      :disabled="disabled"
      :dropdown-options="dropdownOptions"
      :input-options="computedInputOptions"
      @on-input="handleInput"
      @validate="handleValidate"
      @blur="markTouched"
      @enter="$emit('enter')"
    />
    <div v-if="showError" class="phone-validation-message">{{ invalidMessage }}</div>
  </div>
</template>

<script>
import { VueTelInput } from "vue-tel-input";
import { normalizePhoneToE164 } from "@/utils/phone";

export default {
  name: "InternationalPhoneInput",
  components: { VueTelInput },
  props: {
    modelValue: {
      type: String,
      default: "",
    },
    defaultCountry: {
      type: String,
      default: "IN",
    },
    placeholder: {
      type: String,
      default: "Enter phone number",
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    submitted: {
      type: Boolean,
      default: false,
    },
    inputClass: {
      type: String,
      default: "form-control",
    },
    invalidMessage: {
      type: String,
      default: "Enter a valid phone number.",
    },
  },
  emits: ["update:modelValue", "validity-change", "enter"],
  data() {
    return {
      localPhone: this.modelValue || "",
      touched: false,
      isValid: !this.required && !this.modelValue,
    };
  },
  computed: {
    dropdownOptions() {
      return {
        showFlags: true,
        showDialCodeInList: true,
        showDialCodeInSelection: true,
        showSearchBox: true,
      };
    },
    computedInputOptions() {
      return {
        autocomplete: "tel",
        placeholder: this.placeholder,
        required: this.required,
        type: "tel",
        styleClasses: this.inputClass,
      };
    },
    showError() {
      return (this.touched || this.submitted) && !this.isValid;
    },
  },
  watch: {
    modelValue(newValue) {
      if ((newValue || "") !== (this.localPhone || "")) {
        this.localPhone = newValue || "";
      }
      this.syncValidity(newValue || "");
    },
  },
  mounted() {
    this.syncValidity(this.modelValue || "");
  },
  methods: {
    e164FromPhoneObject(phoneObject, value) {
      const candidate = phoneObject?.number || phoneObject?.formatted || value;
      const normalized = normalizePhoneToE164(candidate, this.defaultCountry);
      return normalized.valid ? normalized.e164 : "";
    },
    handleInput(value, phoneObject) {
      const e164 = this.e164FromPhoneObject(phoneObject, value);
      this.isValid = Boolean(e164) || (!this.required && !value);
      if (e164 || !value) {
        this.$emit("update:modelValue", e164);
      }
      this.$emit("validity-change", this.isValid);
    },
    handleValidate(phoneObject) {
      const e164 = this.e164FromPhoneObject(phoneObject, this.localPhone);
      this.isValid = Boolean(e164) || (!this.required && !this.localPhone);
      if (e164 && e164 !== this.modelValue) {
        this.$emit("update:modelValue", e164);
      }
      this.$emit("validity-change", this.isValid);
    },
    syncValidity(value) {
      const normalized = normalizePhoneToE164(value, this.defaultCountry);
      this.isValid = normalized.valid || (!this.required && !value);
      this.$emit("validity-change", this.isValid);
    },
    markTouched() {
      this.touched = true;
    },
    validate() {
      this.touched = true;
      this.syncValidity(this.localPhone);
      return this.isValid;
    },
  },
};
</script>

<style scoped>
.international-phone-field :deep(.vue-tel-input) {
  min-height: 38px;
  border: 1px solid #dbe0e6;
  border-radius: 5px;
  background: #fff;
}

.international-phone-field :deep(.vti__input) {
  width: 100%;
  min-width: 0;
}

.international-phone-field :deep(.vti__dropdown-list) {
  z-index: 1200;
  max-width: min(360px, 92vw);
}

.phone-invalid :deep(.vue-tel-input) {
  border-color: #dc3545;
}

.phone-validation-message {
  margin-top: 4px;
  color: #dc3545;
  font-size: 12px;
}
</style>
